<template>
    <div class="acm-menu">
        <Tooltip
            ref="trigger"
            :text="$t('dash.post-creator.media-editor.timeline.add-clip')"
            :classCustom="'acm-trigger' + (open ? ' open' : '')"
            @click="toggleOpen"
        >
            <i class="fa-solid fa-plus"></i>
        </Tooltip>

        <div class="acm-dropdown" v-if="open" :style="dropdownStyle" @click.stop>
            <div class="acm-option" @click="selectFile">
                <i class="fa-solid fa-file-arrow-up"></i>
                <span>{{ $t('dash.post-creator.media-editor.timeline.add-file') }}</span>
            </div>
            <div class="acm-option acm-option-blank">
                <i class="fa-regular fa-image"></i>
                <span @click="selectBlank">{{ $t('dash.post-creator.media-editor.timeline.add-blank-image') }}</span>
                <input
                    type="color"
                    class="acm-color"
                    :value="color"
                    :title="$t('dash.post-creator.media-editor.timeline.blank-image-color')"
                    @click.stop
                    @input="e => color = e.target.value"
                >
            </div>
        </div>
    </div>
</template>

<script>
import Tooltip from '@/components/dash/tooltip/tooltipInit.vue'

// Single entry point (one "+" button) for the two ways to add a clip - a real uploaded file, or a
// plain solid-color placeholder image with a user-chosen color - instead of two separate
// always-visible buttons competing for the same small space. Used both at the end of the strip
// and in each clip's own hover-revealed "insert after me" control (see TimelineClip.vue), so the
// choice/color logic only lives in one place.
export default {
    name: 'AddClipMenu',

    components: {
        Tooltip
    },

    data() {
        return {
            open: false,
            color: '#000000',
            dropdownStyle: {}
        }
    },

    watch: {
        // Repositions on scroll/resize while open - the timeline card scrolls the trigger itself
        // (see updatePosition's own comment on why the dropdown is fixed at all), so its computed
        // position would otherwise go stale the moment either happens. `true` (capture phase) is
        // required for scroll - it's one of the few DOM events that doesn't bubble, so a listener
        // on document in the default bubble phase would never see a scroll happening inside a
        // nested scrollable ancestor at all.
        open(isOpen) {
            if (isOpen) {
                document.addEventListener('mousedown', this.onDocMouseDown)
                window.addEventListener('resize', this.updatePosition)
                document.addEventListener('scroll', this.updatePosition, true)
            } else {
                document.removeEventListener('mousedown', this.onDocMouseDown)
                window.removeEventListener('resize', this.updatePosition)
                document.removeEventListener('scroll', this.updatePosition, true)
            }
        }
    },

    beforeDestroy() {
        document.removeEventListener('mousedown', this.onDocMouseDown)
        window.removeEventListener('resize', this.updatePosition)
        document.removeEventListener('scroll', this.updatePosition, true)
    },

    methods: {
        toggleOpen() {
            if (this.open) {
                this.open = false
                return
            }
            this.updatePosition()
            this.open = true
        },

        // position: fixed (see .acm-dropdown in timeline.scss) instead of the usual
        // position:absolute-relative-to-a-positioned-ancestor - .videoTimeline itself scrolls
        // (overflow-y:auto, for the compact/expanded toggle), so a dropdown positioned relative to
        // anything inside it only ever painted within whatever portion of that card happened to
        // already be scrolled into view, and disappeared entirely otherwise. Computed from the
        // trigger's own live screen position instead, so it's always fully visible regardless of
        // how the timeline is scrolled or expanded.
        updatePosition() {
            const rect = this.$refs.trigger.$el.getBoundingClientRect()
            const width = 190
            const margin = 8
            let left = rect.left + rect.width / 2 - width / 2
            left = Math.min(Math.max(margin, left), window.innerWidth - width - margin)

            const estimatedHeight = 90
            const openUpward = window.innerHeight - rect.bottom < estimatedHeight + margin && rect.top > estimatedHeight + margin

            this.dropdownStyle = openUpward
                ? { left: `${left}px`, bottom: `${window.innerHeight - rect.top + margin}px` }
                : { left: `${left}px`, top: `${rect.bottom + margin}px` }
        },

        onDocMouseDown(e) {
            if (!this.$el.contains(e.target)) this.open = false
        },

        selectFile() {
            this.open = false
            this.$emit('pick-file')
        },

        selectBlank() {
            this.open = false
            this.$emit('pick-blank', this.color)
        }
    }
}
</script>

<style lang="scss" scoped></style>
